import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';

import { AisSharedModule } from '../shared/ais-shared.module';
import { VfSearchControlComponent } from './toolbar/controls/toolbar-search-controls';
import { VfPortCallControlComponent } from './toolbar/controls/toolbar-port-call-controls';
import { VfExpectedArrivalsControlComponent } from './toolbar/controls/toolbar-expected-arrivals-controls';
import { VfWatchedVesselsControlComponent } from './toolbar/controls/toolbar-vessel-controls';
import { VF_TOOLBAR_CONTROL_PROVIDERS } from './toolbar/vf-toolbar-controls.providers';
import { VF_TOOLBAR_CONFIG_PROVIDERS } from './toolbar/vf-toolbar.config';
import { VesselFinderCreditRefundComponent } from './panels/credit-refund/vesselfinder-credit-refund.component';
import { VfVesselHistoryComponent } from './panels/vessel-history/vf-vessel-history.component';

/**
 * VESSELFINDER — everything this provider contributes to the map.
 *
 * Now at UI parity with Voyage Radar's own toolbar/popup/drawer grammar:
 *
 *   - `VfLocatedLayer` places one amber diamond marker per located/searched
 *     hull, popup built by `vessel-popup.ts` using the SAME shared
 *     `ais-vessel-popup`/`ais-popup__*` classes Voyage Radar/AISstream use.
 *     Master, Voyage and Watch/un-watch are icon buttons on that popup, not
 *     on the search drawer's rows (matching Voyage Radar, whose search drawer
 *     never had per-row action buttons either). Port calls is NOT among them:
 *     it takes identifier LISTS and a filter set no card button can express,
 *     so it lives only in its own drawer.
 *   - `VfSearchControlComponent` mirrors Voyage Radar's search drawer shape:
 *     a free "on the map" list filtered live as you type, then one paid
 *     "Search worldwide" button — adapted only where the capability itself
 *     differs (an exact MMSI/IMO resolution, not a name match; no drawn AOI
 *     snapshot to filter against, so "on the map" means "already located this
 *     session").
 *   - `VfPortCallControlComponent` / `VfExpectedArrivalsControlComponent` /
 *     `VfWatchedVesselsControlComponent` are standing drawer buttons. Port
 *     Calls and Expected Arrivals are the two filter FORMS — same `ea-field`
 *     grammar, same form/results toggle — because both vendor endpoints take
 *     a parameter set rather than one identifier. Watched Vessels still
 *     mirrors Voyage Radar's `VesselHistoryControlComponent`.
 *   - No `playback`/`track` bar, no snapshot/refresh-history panels — there is
 *     no archive to replay (`/livedata` deferred, overview §1.1/§4).
 *   - No `panels/vessel-types` UI — the ship-type filter needs a snapshot
 *     layer to filter, which this phase does not draw.
 *   - `panels/credit-refund` IS built (`VesselFinderCreditRefundComponent`),
 *     but has no open() trigger yet — the "refund" icon that opens Voyage
 *     Radar's equivalent lives inside `leaflet-newcollections.component.ts`,
 *     deferred to a follow-up.
 *
 * One line in `app.module.ts` is still the entire footprint this module adds.
 */
const DECLARATIONS = [
  VfSearchControlComponent,
  VfPortCallControlComponent,
  VfExpectedArrivalsControlComponent,
  VfWatchedVesselsControlComponent,
  VesselFinderCreditRefundComponent,
  VfVesselHistoryComponent,
];

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    // `app-toolbar-drawer`, for the search control's overlay panel — the same
    // shared drawer chrome Voyage Radar's search uses.
    AisSharedModule,
  ],
  declarations: [...DECLARATIONS],
  // `VfVesselHistoryComponent` is mounted from `leaflet-newcollections.component.html`,
  // outside this module — same reason `voyage-radar.module.ts` exports its
  // own sidebar panels.
  exports: [VfVesselHistoryComponent],
  providers: [...VF_TOOLBAR_CONFIG_PROVIDERS, ...VF_TOOLBAR_CONTROL_PROVIDERS],
})
export class VesselFinderModule {}
